import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { SessionUser, WhitelistFormData } from '../types';
import { ArrowIcon, DiscordIcon } from './Icons';

const empty: WhitelistFormData = {
  firstName: '', lastName: '', fullName: '', birthday: '', age: '', country: '', city: '', address: '', mcUsername: '',
  edition: '', microphone: '', smpExperience: '', discovery: '', playstyle: '', agreement: false
};

function calculateAge(dob: string): number {
  if (!dob) return 0;
  const birth = new Date(dob);
  if (isNaN(birth.getTime())) return 0;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
}

export function WhitelistFlow({ user, onSubmitted }: { user: SessionUser; onSubmitted: (user: SessionUser) => void }) {
  const [stage, setStage] = useState<'gate' | 'form'>('gate');
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const steps = ['Personal details', 'Minecraft profile', 'Your story & pledge'];
  const requiredForStep = useMemo(() => [
    ['firstName', 'lastName', 'fullName', 'birthday', 'country', 'city'],
    ['mcUsername', 'edition', 'microphone'],
    ['smpExperience', 'discovery', 'playstyle']
  ] as (keyof WhitelistFormData)[][], []);

  const set = (key: keyof WhitelistFormData, value: string | boolean) => {
    setForm((current) => {
      const updated = { ...current, [key]: value };
      if (key === 'birthday' && typeof value === 'string') {
        const computed = calculateAge(value);
        updated.age = computed > 0 ? String(computed) : '';
      }
      return updated;
    });
  };

  const next = () => {
    const missing = requiredForStep[step].some((key) => !String(form[key]).trim());
    if (missing) return setError('Complete each highlighted field to continue.');
    if (step === 0) {
      const computedAge = calculateAge(form.birthday);
      if (!form.birthday || computedAge < 13 || computedAge > 100) {
        return setError('Enter a valid birthday. Minimum age to join is 13 years old.');
      }
    }
    if (step === 1 && !/^[A-Za-z0-9_]{3,16}$/.test(form.mcUsername)) return setError('Minecraft username must be 3–16 letters, numbers, or underscores.');
    setError(''); setStep((value) => Math.min(value + 1, 2));
  };
  const submit = async () => {
    if (!form.agreement) return setError('You must agree to the official rules before submitting.');
    const computedAge = calculateAge(form.birthday);
    if (!form.birthday || computedAge < 13 || computedAge > 100) {
      return setError('Invalid birthday or applicant does not meet age requirements (13+).');
    }
    setBusy(true); setError('');
    try {
      const response = await fetch('/api/whitelist/apply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, age: computedAge })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Application could not be submitted.');
      onSubmitted(data.user);
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'Something went wrong.'); }
    finally { setBusy(false); }
  };

  if (stage === 'gate') return (
    <div className="gatekeeper">
      <div className="modal-kicker">CITIZENSHIP PROTOCOL</div>
      <h2>Before you apply.</h2>
      <p>Your Discord identity is verified as <strong>@{user.username}</strong>. Read the Constitution, then submit one honest application. A working microphone is required for the interview.</p>
      <div className="gate-grid">
        <Link className="gate-option" to="/rules"><span>01</span><b>Read server rules</b><small>English + සිංහල constitution</small><ArrowIcon /></Link>
        <button className="gate-option accent" onClick={() => setStage('form')}><span>02</span><b>Proceed to form</b><small>About 4 minutes</small><ArrowIcon /></button>
      </div>
    </div>
  );

  return (
    <div className="application-form">
      <div className="form-head">
        <div><div className="modal-kicker">WHITELIST APPLICATION</div><h2>{steps[step]}</h2></div>
        <span>0{step + 1} / 03</span>
      </div>
      <div className="progress"><i style={{ width: `${((step + 1) / 3) * 100}%` }} /></div>

      {step === 0 && <div className="field-grid">
        <Field label="First name" value={form.firstName} onChange={(v) => set('firstName', v)} />
        <Field label="Last name" value={form.lastName} onChange={(v) => set('lastName', v)} />
        <Field label="Full name" value={form.fullName} onChange={(v) => set('fullName', v)} wide />
        <Field label="Birthday / උපන්දිනය" type="date" value={form.birthday} onChange={(v) => set('birthday', v)} hint={form.age ? `Calculated Age: ${form.age} years old` : 'Select your date of birth'} />
        <Field label="Country / රට" value={form.country} onChange={(v) => set('country', v)} />
        <Field label="Hometown / නගරය" value={form.city} onChange={(v) => set('city', v)} />
        <Field label="Residential address" hint="Optional" value={form.address} onChange={(v) => set('address', v)} wide />
      </div>}

      {step === 1 && <div className="field-grid">
        <Field label="Exact Minecraft username" hint="3–16 characters" value={form.mcUsername} onChange={(v) => set('mcUsername', v)} wide />
        <Select label="Edition / platform" value={form.edition} onChange={(v) => set('edition', v)} options={['Java PC', 'Bedrock Mobile', 'Bedrock Windows']} />
        <div className="field"><label>Working microphone? <em>Required for interview</em></label><div className="toggle-row">
          {['Yes', 'No'].map((choice) => <button type="button" key={choice} className={form.microphone === choice ? 'active' : ''} onClick={() => set('microphone', choice)}>{choice}</button>)}
        </div></div>
      </div>}

      {step === 2 && <div className="field-grid">
        <TextArea label="Past SMP experience / කලින් SMP අත්දැකීම්" value={form.smpExperience} onChange={(v) => set('smpExperience', v)} />
        <Select label="How did you hear about us?" value={form.discovery} onChange={(v) => set('discovery', v)} options={['TikTok', 'YouTube', 'Friend', 'Discord', 'Facebook', 'Other']} />
        <TextArea label="Your playstyle & why you want to join" value={form.playstyle} onChange={(v) => set('playstyle', v)} placeholder="Builder, engineer, merchant, PvP, roleplay…" />
        <label className="check-field"><input type="checkbox" checked={form.agreement} onChange={(e) => set('agreement', e.target.checked)} /><span>I have read and agree to all Uge Appage SMP & Discord Rules.<small>මම සියලුම නීති රීති කියවා එකඟ වෙමි.</small></span></label>
      </div>}
      {error && <p className="form-error">{error}</p>}
      <div className="form-actions">
        {step > 0 ? <button className="text-btn" type="button" onClick={() => { setError(''); setStep(step - 1); }}>← Back</button> : <button className="text-btn" type="button" onClick={() => setStage('gate')}>← Gate</button>}
        {step < 2 ? <button className="primary-btn" type="button" onClick={next}>Continue <ArrowIcon /></button> : <button className="primary-btn discord" type="button" onClick={submit} disabled={busy}><DiscordIcon /> {busy ? 'Submitting…' : 'Submit application'}</button>}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, hint, wide, type = 'text' }: { label: string; value: string; onChange: (value: string) => void; hint?: string; wide?: boolean; type?: string }) {
  return <label className={`field ${wide ? 'wide' : ''}`}><span>{label}{hint && <em>{hint}</em>}</span><input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={!hint} /></label>;
}
function Select({ label, value, onChange, options }: { label: string; value: string; onChange: (value: string) => void; options: string[] }) {
  return <label className="field"><span>{label}</span><select value={value} onChange={(e) => onChange(e.target.value)}><option value="">Select one</option>{options.map((option) => <option key={option}>{option}</option>)}</select></label>;
}
function TextArea({ label, value, onChange, placeholder }: { label: string; value: string; onChange: (value: string) => void; placeholder?: string }) {
  return <label className="field wide"><span>{label}</span><textarea value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={4} /></label>;
}
